import React, { createContext, useContext, useEffect, useState } from "react";
import api, { tokenStore } from "@/lib/api";
import { getDeviceId, getUserAgent, guessDeviceName } from "@/lib/device";

const AuthContext = createContext(null);

function userFromResponse(data) {
  return {
    id: data.id, email: data.email, name: data.name, role: data.role,
    allowed_pages: data.allowed_pages || [], can_see_costs: !!data.can_see_costs,
    is_active: data.is_active !== false,
    visibility: data.visibility || {},
    kpis: data.kpis || {},
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    const onExpired = () => { tokenStore.clear(); setUser(false); };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  const _deviceParams = () => ({
    device_id: getDeviceId(),
    device_name: guessDeviceName(),
    user_agent: getUserAgent(),
  });

  const login = async (email, password) => {
    // Yeni akış: response.status ∈ {"ok", "verification_required"}
    const { data } = await api.post("/auth/login", { email, password, ..._deviceParams() });
    if (data.status === "ok") {
      setUser(userFromResponse(data));
    }
    return data;
  };

  const verifyEmail = async (email, code) => {
    // status ∈ {"ok", "pending_admin_approval"}
    const { data } = await api.post("/auth/verify-email", {
      email, code, ..._deviceParams(),
    });
    if (data.status === "ok") {
      setUser(userFromResponse(data));
    }
    return data;
  };

  const resendCode = async (email, password) => {
    const { data } = await api.post("/auth/resend-code", { email, password, ..._deviceParams() });
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(userFromResponse(data));
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      // logout API zaten expire olmuş token ile 401 dönebilir — normal
      if (process.env.NODE_ENV === "development") console.debug("logout API skipped:", err?.message);
    }
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, verifyEmail, resendCode, register, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiError(detail) {
  if (detail == null) return "Bir hata oluştu. Tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
